import {useEffect, useRef} from 'react';
import {Popover, Button, Scrollable} from '@shopify/polaris';
import {FilterIcon} from '@shopify/polaris-icons';
import {useI18n} from '@shopify/react-i18n';
import {useToggle} from '@shopify/react-hooks';

import type {FilterInterface} from './types';
import {FilterList, FilterTags, FilterSearch} from './components';
import styles from './Filters.scss';

interface UseFiltersOptions {
  filters: FilterInterface[];
  disabled?: boolean;
  iconOnly?: boolean;
  onOpen?(): void;
  onClose?(): void;
}

export function Filters({
  filters,
  disabled,
  iconOnly,
  onOpen,
  onClose,
}: UseFiltersOptions) {
  const [i18n] = useI18n();
  const {value: active, toggle: toggleActive} = useToggle(false);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (active && onOpenRef.current) {
      onOpenRef.current();
    }

    if (!active && onCloseRef.current) {
      onCloseRef.current();
    }
  }, [active]);

  const filterItemsMarkup =
    filters &&
    filters.map(({key, filter, hideClearButton, onClear}) => (
      <FilterList.Item
        key={key}
        filter={filter}
        hideClearButton={hideClearButton}
        onClear={onClear}
      />
    ));

  const popoverAccessibilityLabel = iconOnly
    ? i18n.translate('filter')
    : undefined;
  const popoverActivatorContent = iconOnly
    ? undefined
    : i18n.translate('filter');

  const popoverActivatorMarkup = (
    <Button
      onClick={toggleActive}
      disabled={disabled}
      icon={FilterIcon}
      accessibilityLabel={popoverAccessibilityLabel}
      size="large"
    >
      {popoverActivatorContent}
    </Button>
  );

  return (
    <Popover
      active={active}
      onClose={toggleActive}
      activator={popoverActivatorMarkup}
    >
      <div
        className={styles.Popover}
        aria-label={i18n.translate('filtersAccessibilityLabel')}
      >
        <Scrollable>
          <Popover.Section>
            <FilterList>{filterItemsMarkup}</FilterList>
          </Popover.Section>
        </Scrollable>
      </div>
    </Popover>
  );
}

Filters.Search = FilterSearch;
Filters.Tags = FilterTags;
